import type { ReactNode } from 'react';
import type { Contract, RatedLineItem, RatedPeriod } from '@traceio-org/platform-contracts';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { formatMoney, formatTokens, formatUnitPrice } from '../../../billing/money';
import { AdminProblem } from '../../../components/AdminProblem';
import { LoadingBlock } from '../../../components/feedback';
import { DateTimeText, ScrollTable, StatusBadge } from '../../../components/records';
import { useAdmin } from '../../../context';
import { LINE_ITEM_KIND_LABELS, formatAuditValue, formatCount, plural } from '../../../format';
import { useAdminRead } from '../../../useAdminRead';
import { periodLabel } from './shared';

function quantityText(quantity: RatedLineItem['quantity']): ReactNode {
  if (!quantity) return <span className="admin-muted">n/a</span>;
  switch (quantity.unit) {
    case 'millitoken':
      return `${formatTokens(quantity.value)} tokens`;
    case 'carrier_connection':
      return plural(quantity.value, 'carrier connection', 'carrier connections');
    case 'period':
      return plural(quantity.value, 'period', 'periods');
  }
}

function DetailText({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail);
  if (entries.length === 0) return null;
  return (
    <span className="admin-cell-secondary">
      {entries.map(([key, value], index) => (
        <span key={key}>
          {index > 0 ? '; ' : null}
          {key}: {formatAuditValue(value)}
        </span>
      ))}
    </span>
  );
}

function LinesTable({ period }: { period: RatedPeriod }) {
  return (
    <ScrollTable caption="Line items" className="admin-table admin-table--line-items">
      <thead>
        <tr>
          <th scope="col" className="admin-num">
            #
          </th>
          <th scope="col">Kind</th>
          <th scope="col">Description</th>
          <th scope="col">Quantity</th>
          <th scope="col" className="admin-num">
            Unit price
          </th>
          <th scope="col" className="admin-num">
            Amount
          </th>
        </tr>
      </thead>
      <tbody>
        {period.lines.map((line) => (
          <tr key={line.position}>
            <th scope="row" className="admin-num">
              {formatCount(line.position)}
            </th>
            <td>
              {LINE_ITEM_KIND_LABELS[line.kind] ?? line.kind}
              {line.componentKey ? (
                <span className="admin-cell-secondary">
                  <code>{line.componentKey}</code>
                </span>
              ) : null}
            </td>
            <td className="admin-cell-wrap">
              {line.description}
              <DetailText detail={line.detail} />
            </td>
            <td>{quantityText(line.quantity)}</td>
            <td className="admin-num">
              {line.unitPriceMicros === null ? (
                <span className="admin-muted">n/a</span>
              ) : (
                formatUnitPrice(line.unitPriceMicros, period.currency)
              )}
            </td>
            <td className={line.amountMicros < 0 ? 'admin-num admin-credit' : 'admin-num'}>
              {formatMoney(line.amountMicros, period.currency)}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <th scope="row" colSpan={5}>
            Total
          </th>
          <td className="admin-num">{formatMoney(period.totalMicros, period.currency)}</td>
        </tr>
      </tfoot>
    </ScrollTable>
  );
}

export interface LineItemsSectionProps {
  organizationId: string;
  contract: Contract;
  periodStart: string | undefined;
}

/**
 * The line items of one period of a contract: as rated and stored when the period is closed (they never change), a
 * preview of what happened so far when it is open (stored nowhere). Amounts, quantities and unit prices are
 * platform-api's; the total is its too.
 */
export function LineItemsSection({ organizationId, contract, periodStart }: LineItemsSectionProps) {
  const { api } = useAdmin();
  const key = `line-items\u0000${organizationId}\u0000${contract.id}\u0000${periodStart ?? ''}`;
  const read = useAdminRead(key, (signal) =>
    api.getBillingLineItems(
      organizationId,
      { contractId: contract.id, ...(periodStart ? { periodStart } : {}) },
      signal,
    ),
  );
  const period = read.dataKey === key && !read.failure ? read.data : null;

  let body: ReactNode;
  if (read.failure?.kind === 'not-found') {
    body = (
      <EmptyPageState
        icon={<NavigationGlyph name="FileWarning" />}
        title="No period to rate"
        description="The contract has no period yet (it has not started), or no period of it starts at the chosen date."
      />
    );
  } else if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="the line items" onRetry={read.retry} />;
  } else if (!period) {
    body = <LoadingBlock label="Loading the line items" />;
  } else {
    body = (
      <div className="admin-stack">
        <p className="admin-period-status">
          <StatusBadge status={period.status} />{' '}
          <span>
            {periodLabel({ start: period.periodStart, end: period.periodEnd, kind: period.kind })}.{' '}
            {period.status === 'closed' && period.ratedAt ? (
              <>
                Rated <DateTimeText iso={period.ratedAt} />; these lines never change.
              </>
            ) : (
              'A preview of the lines so far; nothing is stored until the period is rated.'
            )}
            {period.kind === 'trial' ? ' A trial charges nothing: its usage is shown, then credited.' : ''}
          </span>
        </p>
        {period.lines.length === 0 ? (
          <p className="admin-muted">No line item in this period.</p>
        ) : (
          <LinesTable period={period} />
        )}
      </div>
    );
  }

  return (
    <PageSection
      title="Line items"
      description="The bill of the period: the base, the purchased connections, the token line, discounts, adjustments and the trial credit."
    >
      {body}
    </PageSection>
  );
}
